import { useEffect, useMemo, useState } from 'react';
import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useUpdateEffect } from 'ahooks';
import { SortBy } from 'tea-component/lib/table/addons';
import { getCreativeTrendsList } from '@opm/services';
import { useStableValue } from '@opm/utils/stableKey';
import { ITrendItem, TStatusTab, IListMax } from '../types';
import { DEFAULT_PAGE_INFO } from '../constants';

const VIEW_MODE_STORAGE_KEY = 'creative_trends_view_mode';

function getStoredViewMode(fallback: 'card' | 'table'): 'card' | 'table' {
  try {
    const stored = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored === 'card' || stored === 'table') return stored;
  } catch {
    /* ignore */
  }
  return fallback;
}

/**
 * Creative Trends 列表数据 Hook（双模式）
 *
 * 设计原则（参照 useCompetitorsAdCreativesData）：
 * - Card 模式：下拉加载（infinite scroll），累积所有页数据
 * - Table 模式：分页器翻页，只取当前页数据
 * - 视图切换、筛选条件、Tab、排序、搜索切换时：清空数据，重置 page:1 重新请求
 */
export function useCreativeTrendsData({
  filter,
  defaultPageInfo = DEFAULT_PAGE_INFO,
  defaultViewMode = 'card',
  closeDrawer,
}: {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
  defaultViewMode?: 'card' | 'table';
  closeDrawer: () => void;
}) {
  // ─── StatusTab 状态 ───
  const [statusTabRef, statusTab, setStatusTab] =
    useStateRef<TStatusTab>('hot');

  // ─── 视图模式（Session 级别记忆偏好） ───
  const [viewMode, setViewMode] = useState<'card' | 'table'>(() =>
    getStoredViewMode(defaultViewMode),
  );

  // ─── 搜索关键字（在 useMemo 闭包中使用） ───
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');

  // ─── 排序（在 useMemo 闭包中使用） ───
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'impressions',
  });

  // ─── 分页 ───
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({ defaultPageInfo });

  // ─── 双模式数据存储 ───
  const [cardDataRef, cardRecords, setCardData] = useStateRef<ITrendItem[]>([]);
  const [tableRecords, setTableRecords] = useState<ITrendItem[]>([]);
  const [total, setTotal] = useState(0);
  /** 当前 tab + filter 下全量 KPI 最大值（用于 Card View 进度条归一化） */
  const [listMax, setListMax] = useState<IListMax>({
    cumulative_impressions: 0,
    creatives: 0,
    avg_days_active: 0,
  });

  // ─── 请求 ───
  const { wrapperCancelRequest, loading, loadingRef } = useCancelRequestHook({
    clear: () => {
      setTableRecords([]);
      setTotal(0);
      setListMax({
        cumulative_impressions: 0,
        creatives: 0,
        avg_days_active: 0,
      });
    },
  });

  // ─── 重置所有数据 + 分页（筛选条件或视图切换时调用） ───
  function resetAllData() {
    setCardData([]);
    setTableRecords([]);
    setTotal(0);
    setListMax({ cumulative_impressions: 0, creatives: 0, avg_days_active: 0 });
    resetPageInfo();
    closeDrawer();
  }

  // ─── 切换 StatusTab ───
  function changeStatusTab(tab: TStatusTab) {
    if (tab === statusTabRef.current) return;
    setStatusTab(tab);
    resetAllData();
  }

  // ─── 切换排序 ───
  function changeSortBy(newSortBy: SortBy<string>) {
    setSortBy(newSortBy);
    resetAllData();
  }

  // ─── 切换搜索关键字 ───
  function changeKeyword(val: string) {
    if (val === keywordRef.current) return;
    setKeyword(val);
    resetAllData();
  }

  // ─── 切换视图模式 ───
  // 切换时清空数据，从 page:1 重新加载；同时持久化到 sessionStorage
  function changeViewMode(mode: 'card' | 'table') {
    if (mode === viewMode) return;
    setViewMode(mode);
    try {
      sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch (_e) {
      /* ignore */
    }
    setCardData([]);
    setTableRecords([]);
    setTotal(0);
    setListMax({ cumulative_impressions: 0, creatives: 0, avg_days_active: 0 });
    resetPageInfo();
  }

  // ─── Card View 触底加载下一页 ───
  function loadNextPage() {
    if (loadingRef.current) return;
    if (viewMode !== 'card') return;
    const loadedCount = cardDataRef.current?.length || 0;
    if (loadedCount >= total && total > 0) return;
    nextPage();
  }

  // ─── Table View 翻页 ───
  function changeTablePageInfo(newPageInfo?: {
    page: number;
    page_size: number;
  }) {
    setTableRecords([]);
    changePageInfo(newPageInfo);
  }

  // ─── 稳定输入引用 ───
  const stableFilter = useStableValue(filter);
  const stablePageInfo = useStableValue(pageInfo);

  // ─── 筛选条件变化时重置 ───
  useUpdateEffect(() => {
    if (!stableFilter) return;
    resetAllData();
  }, [stableFilter]);

  // ─── 完整请求参数 ───
  const params = useMemo(() => {
    if (!stableFilter) return null;
    return {
      ...stableFilter,
      ...stablePageInfo,
      tab: statusTabRef.current,
      sort_item: sortByRef.current?.by,
      sort_model: sortByRef.current?.order,
      search_text: keywordRef.current,
    };
  }, [stableFilter, stablePageInfo, statusTab, sortBy, keyword]);

  // ─── 发起请求 ───
  function request() {
    if (!params) return;
    wrapperCancelRequest(getCreativeTrendsList, params)
      .then((response: any) => {
        const { list = [], total: totalCount = 0, max } = response || {};

        if (viewMode === 'card') {
          // Card 模式：累积数据
          if (pageInfo.page === 1) {
            setCardData(list);
          } else {
            setCardData([...(cardDataRef.current || []), ...list]);
          }
        } else {
          // Table 模式：替换当前页数据
          setTableRecords(list);
        }

        setTotal(totalCount);
        setListMax(max);
      })
      .catch(() => {
        // 请求被取消或失败，Card 模式回退页码
        if (viewMode === 'card' && pageInfo.page > 1) {
          const prevPage = Math.max(
            1,
            Math.ceil((cardDataRef.current?.length || 0) / pageInfo.page_size),
          );
          changePageInfoNoRender({ ...pageInfo, page: prevPage });
        }
      });
  }

  useEffect(() => {
    if (!params) return;
    request();
  }, [params, viewMode]);

  // ─── 当前视图的数据 ───
  const currentRecords = viewMode === 'card' ? cardRecords || [] : tableRecords;
  const hasMore = (cardRecords?.length || 0) < total;

  // ─── 状态判断 ───
  const isEmpty = !loading && currentRecords.length === 0;
  const isFirstLoading =
    loading && pageInfo.page === 1 && currentRecords.length === 0;
  const isPaginationLoading =
    loading && viewMode === 'card' && (cardRecords?.length || 0) > 0;

  return {
    // 数据
    cardRecords: cardRecords || [],
    tableRecords,
    total,
    /** 当前 tab + filter 下全量 KPI 最大值（用于 Card View 进度条归一化） */
    listMax,
    hasMore,
    // 分页
    pageInfo,
    changeTablePageInfo,
    loadNextPage,
    // 加载状态
    loading,
    isFirstLoading,
    isEmpty,
    isPaginationLoading,
    // StatusTab
    statusTab: statusTab as TStatusTab,
    setStatusTab: changeStatusTab,
    // 排序
    sortBy: sortBy as SortBy<string>,
    setSortBy: changeSortBy,
    // 搜索
    keyword: keyword || '',
    setKeyword: changeKeyword,
    // 视图模式
    viewMode,
    setViewMode: changeViewMode,
  };
}
